/**
 * SDMS Template Builder ▸ Review — the checks that decide whether SDMS Result Entry
 * can run a template (port of Alpaca Studio's reviewUtils, re-pointed at what AI's
 * Result Entry actually needs). Advisory: Save is never blocked by a review finding
 * (the server enforces the hard rules).
 */
import type { TemplateDetail } from './sdmsTemplateApi';
import { effectiveBands, inRange, sameFormula, sheetOf, type WorkbookInfo } from './templateWorkbook';
import type { BandChange } from './sdmsTemplateApi';

export type ReviewLevel = 'error' | 'warning' | 'info';
export interface ReviewFinding { level: ReviewLevel; area: string; message: string }

/** The fields every sample-based SDMS sheet needs to key its rows back to results. */
export const REQUIRED_FIELDS = ['SYSSAMPLECODE', 'PARAMETER', 'RUNNO', 'QCTYPE'] as const;

export function reviewTemplate(t: TemplateDetail, wb: WorkbookInfo, pendingBands: BandChange[], hasWorkbook: boolean): ReviewFinding[] {
  const out: ReviewFinding[] = [];
  const h = t.header;

  if (!hasWorkbook)
    out.push({ level: 'error', area: 'Workbook', message: 'The template has no workbook. Upload the .xlsx layout.' });
  if (t.tests.length === 0)
    out.push({ level: 'error', area: 'Linked Tests', message: 'No test is linked — SDMS Result Entry will not offer this template for any test.' });
  if (h.isActive && h.isRetire)
    out.push({ level: 'error', area: 'General Info', message: 'The template is both Active and Retired.' });
  if (!h.isActive)
    out.push({ level: 'info', area: 'General Info', message: 'The template is not Active — Result Entry will not offer it.' });

  const dataSheet = h.dtSheetID < 1 ? 1 : h.dtSheetID;
  const bands = effectiveBands(wb, pendingBands);
  const detail = bands.find(b => b.sheetIndex === dataSheet && b.name === 'DETAILRANGE');
  if (hasWorkbook && !detail)
    out.push({ level: 'warning', area: 'Bands', message: `Sheet ${dataSheet} has no Detail band — the sample rows will not repeat.` });

  const mappedFields = new Set(t.parsing.map(p => (p.fieldName ?? '').toUpperCase()));
  if (h.model !== 2) {
    const missing = REQUIRED_FIELDS.filter(f => !mappedFields.has(f));
    if (missing.length)
      out.push({ level: 'warning', area: 'Data Parsing', message: `Not mapped: ${missing.join(', ')} — Result Entry keys each row by these.` });
  }

  const seen = new Map<string, number>();
  for (const p of t.parsing) {
    const key = `${sheetOf(p)}!${(p.position ?? '').toUpperCase()}`;
    seen.set(key, (seen.get(key) ?? 0) + 1);
  }
  for (const [key, n] of seen) if (n > 1)
    out.push({ level: 'error', area: 'Data Parsing', message: `${key} is mapped ${n} times.` });

  if (detail) {
    for (const p of t.parsing.filter(x => sheetOf(x) === dataSheet)) {
      const inside = inRange(p.position, detail.range);
      if (inside && !p.continuous)
        out.push({ level: 'warning', area: 'Data Parsing', message: `${p.position} (${p.fieldName}) is inside the Detail band but not Continuous.` });
      if (!inside && p.continuous)
        out.push({ level: 'warning', area: 'Data Parsing', message: `${p.position} (${p.fieldName}) is Continuous but outside the Detail band.` });
    }
  }

  // Workbook ↔ mapping drift. The WORKBOOK formula is what Result Entry runs — on live
  // templates it is routinely newer than the mapping (edited in Excel, e.g. wrapped in
  // IFERROR), so the fix is to take the workbook's formula, never to overwrite it.
  if (hasWorkbook) {
    const cells = new Map(wb.formulas.map(f => [`${f.sheetIndex}!${f.ref}`, f.formula]));
    const drift = t.parsing.filter(p => {
      if (p.uqID <= 0 || !(p.formula ?? '').trim().startsWith('=')) return false;
      const cell = cells.get(`${sheetOf(p)}!${(p.position ?? '').toUpperCase()}`);
      return cell != null && !sameFormula(cell, p.formula);
    });
    if (drift.length)
      out.push({ level: 'info', area: 'Workbook',
        message: `${drift.length} mapped cell(s) have a different formula in the workbook (${drift.slice(0, 8).map(p => p.position).join(', ')}${drift.length > 8 ? '…' : ''}). ` +
          'The workbook formula is the one Result Entry runs — use "Take Formulas from Workbook" on Data Parsing to align the mapping.' });
    const missing = t.parsing.filter(p => p.uqID > 0 && (p.formula ?? '').trim().startsWith('=')
      && !cells.has(`${sheetOf(p)}!${(p.position ?? '').toUpperCase()}`));
    if (missing.length)
      out.push({ level: 'warning', area: 'Workbook',
        message: `${missing.length} mapped cell(s) hold no formula in the workbook (${missing.slice(0, 8).map(p => p.position).join(', ')}${missing.length > 8 ? '…' : ''}), so Result Entry will not fill them. Re-map the field to write it.` });
    const mapped = new Set(t.parsing.map(p => `${sheetOf(p)}!${(p.position ?? '').toUpperCase()}`));
    const orphans = wb.formulas.filter(f => /^=\s*FIELD\(/i.test(f.formula) && !mapped.has(`${f.sheetIndex}!${f.ref}`));
    if (orphans.length)
      out.push({ level: 'info', area: 'Workbook', message: `${orphans.length} FIELD cell(s) in the workbook are merged but not in Data Parsing (they are not saved back): ${orphans.slice(0, 8).map(o => `${wb.sheets[o.sheetIndex - 1] ?? o.sheetIndex}!${o.ref}`).join(', ')}${orphans.length > 8 ? '…' : ''}` });
  }

  const noWrite = t.parsing.filter(p => !p.write).length;
  if (t.parsing.length > 0 && noWrite === t.parsing.length)
    out.push({ level: 'warning', area: 'Data Parsing', message: 'No mapped cell has Write on — Result Entry will save nothing back.' });

  return out;
}
